import { Geolocation, type Position } from '@capacitor/geolocation';
import { Capacitor } from '@capacitor/core';
import { supabase } from './supabaseClient';

export const geolocationService = {
  /** Richiede permessi al volo (solo su mobile). */
  async requestPermissions() {
    if (!Capacitor.isNativePlatform()) return 'granted';
    const status = await Geolocation.checkPermissions();
    if (status.location === 'granted') return status.location;
    const req = await Geolocation.requestPermissions();
    return req.location;
  },

  /** Posizione attuale con timeout. Fallback a `navigator.geolocation` su web. */
  async getCurrentPosition(): Promise<Position> {
    if (Capacitor.isNativePlatform()) {
      return await Geolocation.getCurrentPosition({
        enableHighAccuracy: false, // sufficiente per la nostra app, risparmia batteria
        timeout: 15000,
        maximumAge: 60000,
      });
    }
    // Web fallback (Vite dev server)
    return new Promise((resolve, reject) => {
      if (!('geolocation' in navigator)) {
        reject(new Error('Geolocalizzazione non supportata su questo browser.'));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (p) =>
          resolve({
            coords: {
              latitude: p.coords.latitude,
              longitude: p.coords.longitude,
              accuracy: p.coords.accuracy,
              altitude: p.coords.altitude,
              altitudeAccuracy: p.coords.altitudeAccuracy,
              heading: p.coords.heading,
              speed: p.coords.speed,
            },
            timestamp: p.timestamp,
          } as Position),
        (e) => reject(e),
        { enableHighAccuracy: false, timeout: 15000, maximumAge: 60000 }
      );
    });
  },

  /** Salva la posizione approssimata (privacy-first) via funzione RPC. */
  async saveMyLocation(latitude: number, longitude: number, postalCode?: string, countryCode?: string) {
    // Approssimo arrotondando a 2 decimali (~1 km di precisione). Vedi suggerimento privacy nel doc.
    const approxLat = Math.round(latitude * 100) / 100;
    const approxLon = Math.round(longitude * 100) / 100;
    const { error } = await supabase.rpc('update_my_location', {
      p_latitude: approxLat,
      p_longitude: approxLon,
      p_postal_code: postalCode ?? null,
      p_country_code: countryCode ?? null,
    });
    if (error) throw error;
  },

  async refreshAndSave() {
    const status = await this.requestPermissions();
    if (status === 'denied') {
      throw new Error('geolocation_denied');
    }
    const pos = await this.getCurrentPosition();
    await this.saveMyLocation(pos.coords.latitude, pos.coords.longitude);
    return pos;
  },

  /** Geocodifica un indirizzo testuale via Nominatim → lat/lon approssimati. */
  async geocodeAddress(address: string): Promise<{ lat: number; lon: number } | null> {
    try {
      const url =
        'https://nominatim.openstreetmap.org/search?' +
        new URLSearchParams({
          q: address,
          format: 'json',
          limit: '1',
          'accept-language': 'it,en',
        }).toString();
      const res = await fetch(url, { headers: { 'User-Agent': 'SwipeWish/1.0' } });
      if (!res.ok) return null;
      const data = await res.json();
      if (!Array.isArray(data) || data.length === 0) return null;
      return { lat: parseFloat(data[0].lat), lon: parseFloat(data[0].lon) };
    } catch {
      return null;
    }
  },
};
